import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import SignupModal from './SignupModal';
import PricingModal from './PricingModal';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${theme.colors.background};
  font-family: ${theme.typography.fontFamily};
  overflow-y: auto;
`;

const LoginCard = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xxl};
  padding: ${theme.spacing.xxxl};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
`;

const Title = styled.h2`
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.xxxxl};
  font-weight: ${theme.typography.fontWeights.bold};
  font-family: ${theme.typography.fontFamily};
  letter-spacing: ${theme.typography.letterSpacing.normal};
`;

const Subtitle = styled.p`
  margin: 0 0 ${theme.spacing.lg} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  font-family: ${theme.typography.fontFamily};
  opacity: 0.85;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  width: 100%;
  box-sizing: border-box;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSizes.base};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.error};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.normal};
`;

const MainContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  flex-direction: row;
  gap: ${theme.spacing.xxxxl};
  padding: ${theme.spacing.xxl};
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.xxxl};
  pointer-events: none;
  flex-direction: row;
  align-items: center;
  padding: ${theme.spacing.lg} 0;
  flex-shrink: 0;
`;

const MainLogoContainer = styled.div`
  width: 320px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const MainLogo = styled.img`
  height: 274.76px;
  object-fit: contain;
  filter: ${theme.shadows.dropShadow};
`;

const Logo = styled.img`
  height: 48px;
  object-fit: contain;
  filter: ${theme.shadows.dropShadow};
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.md} 0 ${theme.spacing.xxl} 0;
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  font-family: ${theme.typography.fontFamily};
  flex-shrink: 0;
`;

const FooterLink = styled.a`
  color: ${theme.colors.textSecondary};
  text-decoration: none;
  transition: color ${theme.transitions.fast};
  cursor: pointer;

  &:hover {
    color: ${theme.colors.accent};
    text-decoration: underline;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const ForgotPasswordLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  padding: 0;
  align-self: flex-end;
  margin-top: -${theme.spacing.xs};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accent};
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  color: ${theme.colors.success || '#4ade80'};
  font-size: ${theme.typography.fontSizes.base};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.success || '#4ade80'};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.normal};
`;

const SecondaryText = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.sm};
  margin-top: ${theme.spacing.md};
  margin-bottom: 0;
`;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setIsResetting(true);
    setError('');
    setResetSent(false);

    const result = await resetPassword(email);

    if (result.success) {
      setResetSent(true);
    } else {
      setError(result.error || 'Failed to send reset email');
    }
    setIsResetting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setResetSent(false);

    const result = await login(email, password);

    if (!result.success) {
      setError(result.error || 'Login failed');
    }

    setIsLoading(false);
  };

  return (
    <LoginContainer>
      <MainContent>
        <MainLogoContainer>
          <MainLogo src="/images/logo.svg" alt="Tater Logo" />
        </MainLogoContainer>

        <LoginCard>
          <Title>Welcome to Tater</Title>
          <Subtitle style={{ marginBottom: theme.spacing.xl }}>
            Professional sports video annotation
          </Subtitle>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </FormGroup>
            <ForgotPasswordLink
              type="button"
              onClick={handleForgotPassword}
              disabled={isResetting}
            >
              {isResetting ? 'Sending...' : 'Forgot password?'}
            </ForgotPasswordLink>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {resetSent && (
              <SuccessMessage>
                Password reset email sent. Check your inbox.
              </SuccessMessage>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </Form>

          <ActionButtons>
            <Button
              onClick={() => setIsSignupModalOpen(true)}
              variant="primary"
              style={{
                flex: 1,
                fontWeight: theme.typography.fontWeights.bold,
                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                height: '44px',
                boxSizing: 'border-box'
              }}
            >
              Start Free Trial
            </Button>
            <Button
              onClick={() => setIsPricingModalOpen(true)}
              variant="ghost"
              style={{
                flex: 1,
                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                height: '44px',
                boxSizing: 'border-box'
              }}
            >
              View Pricing
            </Button>
          </ActionButtons>

          <SecondaryText>
            No credit card required • 7-day trial
          </SecondaryText>
        </LoginCard>
      </MainContent>

      <Footer>
        <FooterLink href="https://splitstep.ai/eula.html" target="_blank" rel="noopener noreferrer">
          EULA
        </FooterLink>
        <span>•</span>
        <FooterLink href="https://splitstep.ai/privacy-policy.html" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </FooterLink>
      </Footer>

      <LogoContainer>
        <Logo src="/images/logo%20SplitStep,%20symbol%2003.png" alt="SplitStep Logo" />
        <Logo src="/images/logo%20Wyatt%20AI%20wide,%20dark.png" alt="Wyatt AI Logo" />
      </LogoContainer>

      <SignupModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
      />

      <PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
        onSignupClick={() => {
          setIsPricingModalOpen(false);
          setIsSignupModalOpen(true);
        }}
      />
    </LoginContainer>
  );
};

export default LoginPage; 